import { Container, NextUIProvider, Loading } from "@nextui-org/react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { Suspense, lazy } from "react";

import { AppState, NavigationProvider } from "./context/NavigationContext";
import RouteLogger from "./components/RouteLogger";
import { getTheme } from "./config/skin";
import { MainController } from "./components/MainController";
import { ParticipantProvider } from "./context/ParticipantContext";
import { GATracker } from "./components/GATracker";
import RequireAuth from "./components/RequireAuth";
import DevSupportResolveWorkshopId from "./pages/DevSupport/resolve-workshop-id";
import ErrorPage from "./pages/Error";

// Lazy imports
const HomePage = lazy(() => import("./pages/Home"));
const ScreenPage = lazy(() => import("./pages/Screen"));
const VideoStepPage = lazy(() => import("./pages/VideoStepPage").then(module => ({ default: module.VideoStepPage })));
const TrainingPage = lazy(() => import("./pages/Training").then(module => ({ default: module.TrainingPage })));
const SlideConfirmation = lazy(() => import("./pages/SlideConfirmation").then(module => ({ default: module.SlideConfirmation })));
const EvaluationPage1 = lazy(() => import("./pages/Evaluation1"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const E1ToolsStep = lazy(() => import("./pages/EvaluationShared/E1ToolsStep").then(module => ({ default: module.E1ToolsStep })));
const ClosingRemarksStep = lazy(() => import("./pages/EvaluationShared/ClosingRemarksStep").then(module => ({ default: module.ClosingRemarksStep })));
const ThankYouPage = lazy(() => import("./pages/ThankYou").then(module => ({ default: module.ThankYouPage })));
const WaitingFormationPage = lazy(() => import("./pages/WaitingFormation").then(module => ({ default: module.WaitingFormationPage })));
const VideoConferencePage = lazy(() => import("./pages/VideoConferenceLiveKit"));
const VideoConferenceDummyPage = lazy(() => import("./pages/VideoConferenceDummy"));
const SelectGroupPage = lazy(() => import("./pages/SelectGroup"));
const SoonPage = lazy(() => import("./pages/Soon"));
const LogoutPage = lazy(() => import("./pages/Logout"));
const SurveyPage = lazy(() => import("./pages/Survey"));
const MagicLogin = lazy(() => import("./pages/MagicLogin"));
const WorkshopRecording = lazy(() => import("./pages/WorkshopRecording"));
const CIPage = lazy(() => import("./pages/CI"));
const SelfTestPage = lazy(() => import("./pages/SelfTest").then(module => ({ default: module.SelfTestPage })));
const LiveKitPage = lazy(() => import("./pages/DevSupport/LiveKitDemoPage"));
const VideoGallery = lazy(() => import("./components/VideoGallery/VideoGallery").then(module => ({ default: module.VideoGallery })));
const VideoPlayer = lazy(() => import("./components/VideoPlayer/VideoPlayer").then(module => ({ default: module.VideoPlayer })));
const SurveyTestPage = lazy(() => import("./pages/SurveyTest").then(module => ({ default: module.SurveyTestPage })));

//import RouteLogger from "./components/RouteLogger";
//import WorkshopPage from "./pages/Workshop"; // Revisar

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "chatlio-widget": ChatlioWidgetAttributes;
    }
    interface ChatlioWidgetAttributes {
      widgetid: string;
      widgettype: string;
    }
  }
}

function App() {
  const location = useLocation();
  const showChatlio = location.pathname.startsWith("/workshop/");

  const getWorkshopId = (pathname: string) => {
    const match = pathname.match(/\/workshop(?:-d)?\/([^/]+)/);
    return match ? match[1] : undefined;
  };
  const workshopId = getWorkshopId(location.pathname);

  return (
    <NextUIProvider theme={getTheme(workshopId)}>
      <NavigationProvider>
        <ParticipantProvider>
          <GATracker />
          <RouteLogger />
          <Container md>
            <Suspense fallback={<Container css={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}><Loading /></Container>}>
              <Routes>
                <Route index element={<SoonPage />} />
                <Route path="ci" element={<CIPage />} />
                <Route path="self-test" element={<SelfTestPage />} />
                <Route path="magic-login" element={<MagicLogin />} />
                <Route path="workshop-recording" element={<WorkshopRecording />} />
                <Route path="logout" element={<LogoutPage />} />
                <Route path="workshop/:workshopId">
                  <Route path="*" element={<MainController />} />
                </Route>
                {/* routes only for debugging purposes  */}
                {/* routes not yet confirmed  */}
                {(import.meta.env.VITE_APP_ENV === "dev" || import.meta.env.VITE_APP_ENV === "test" || import.meta.env.VITE_APP_ENV === "local") && (
                  <><Route path="workshop-d/0" element={<DevSupportResolveWorkshopId />} />
                    <Route path="workshop-d/1" element={<DevSupportResolveWorkshopId />} />
                    <Route path="workshop-d/2" element={<DevSupportResolveWorkshopId />} />
                    <Route path="workshop-d/3" element={<DevSupportResolveWorkshopId />} />
                    <Route path="workshop-d/:workshopId">
                      <Route index element={<HomePage />} />
                      <Route element={<RequireAuth />}>
                        <>
                          <Route path="screen" element={<ScreenPage />} />
                          <Route path="select-group" element={<SelectGroupPage />} />
                          {/* core routes  */}
                          <Route
                            path="waiting-formation"
                            element={<WaitingFormationPage />} />
                          <Route
                            path="video/:stateId"
                            element={<VideoStepPage
                              nextRoutePath={AppState.VIDEO} // Placeholder, will be overridden by stateId in VideoStepPage
                            />} />
                          <Route
                            path="survey/:stateId"
                            element={<SurveyPage />} />
                          <Route path="training" element={<TrainingPage />} />
                          <Route
                            path="slide-confirmation"
                            element={<SlideConfirmation />} />
                          <Route
                            path="design"
                            element={<VideoConferencePage currentAppState={AppState.DESIGN} />} />
                          <Route
                            path="design-dummy"
                            element={<VideoConferenceDummyPage currentAppState={AppState.DESIGN_DUMMY} />} />
                          <Route
                            path="pitch"
                            element={<VideoConferencePage currentAppState={AppState.PITCH} pitch="true" />} />
                          <Route
                            path="pitch-dummy"
                            element={<VideoConferenceDummyPage currentAppState={AppState.PITCH_DUMMY} />} />
                          <Route path="thank-you" element={<ThankYouPage />} />
                          <Route path="error" element={<ErrorPage />} />
                        </>
                      </Route>
                    </Route></>
                )}
                <Route path="workshop-x/:workshopId">
                  <Route index element={<HomePage />} />
                </Route>
                {/* routes only for debugging purposes  */}
                {/* routes not yet confirmed  */}
                {(import.meta.env.VITE_APP_ENV === "dev" || import.meta.env.VITE_APP_ENV === "test" || import.meta.env.VITE_APP_ENV === "local") && (
                  <>
                    <Route path="videos" element={<VideoGallery />} />
                    <Route path="videos/:id" element={<VideoPlayer />} />
                    <Route path="livekit" element={<LiveKitPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="video-conf-dummy" element={<VideoConferenceDummyPage currentAppState={AppState.PITCH} />} />
                    <Route path="evaluation1" element={<EvaluationPage1 />} />
                    <Route path="tools" element={<E1ToolsStep />} />
                    <Route
                      path="closing-remarks"
                      element={<ClosingRemarksStep />}
                    />
                    <Route path="survey-test/:surveyId" element={<SurveyTestPage />} />
                  </>
                )}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Container>
          <Toaster
            position="bottom-right"
            toastOptions={{ duration: 4000 }}
            reverseOrder={false}
          />
          {showChatlio && (
            <chatlio-widget
              widgetid="cd9456a1-bbf5-42d8-5ad0-97607b2973ea"
              widgettype="sidebar"
            ></chatlio-widget>
          )}
        </ParticipantProvider>
      </NavigationProvider >
    </NextUIProvider >
  );
}

export default App;
